import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import {
    ECardType,
    EVisibility,
    EWorldMapInitialType,
    EWorldMapMarkerType,
    EWorldMapSetType,
    WorldMapMarkerDetailStyle
} from '__generated__/globalTypes';
import { F_MapMarkerIndoorStandFragment, GuideQuery } from '__generated__v2/graphql';
import { CtxMapV2Provider } from 'components/mapV2/contexts/CtxMapV2';
import { WorldMapStories_publishedStoriesForWorldMap_cards } from 'graphql/query/__generated__/WorldMapStories';
import { WorldMapView_worldMapView } from 'graphql/query/__generated__/worldMapView';
import { useViewMapQueryParams } from 'hooks/map/useViewMapQueryParams';
import { useAnalytics } from 'libs/utils/site/analytics';
import { isDefined } from 'libs/utils/typesafe/isDefined';
import { S_PageStyle } from 'pages/page/PageStyle';
import { OSLO_GUIDE_QUERY } from 'pages/special/OsloDesignFair/fragments';
import { CtxMapViewProvider } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { OsloDesignFairMap } from 'pages/special/OsloDesignFair/OsloDesignFairMap';
import { useContext, useEffect, useState } from 'react';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled, { ThemeProvider } from 'styled-components/macro';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';

interface Props {
    className?: string;
}

const CATEGORY_IMAGES = {
    'c973873e-55ae-479c-b2ab-973f34188649':
        'https://dvtqxks0zalev.cloudfront.net/9c3b8fde-a882-4eda-8765-073fc8976c65.png',
    'b0faf63d-80ed-4220-8099-9cc2227ab9a2':
        'https://dvtqxks0zalev.cloudfront.net/998c7a7a-f4c8-470c-b472-140d56fa82c4.png',
    '21d8cfed-5f3d-442c-bcd7-0f90c4ed8aaf':
        'https://dvtqxks0zalev.cloudfront.net/a4984ce5-e3e9-4e25-a075-7e0da5a59e41.png',
    'f01032fc-5a95-4f28-8ba2-9bc381e345a1':
        'https://dvtqxks0zalev.cloudfront.net/d95d0aa4-965b-4d73-96b7-a4117aa909e1.png',
    'e1b41f7d-107f-4760-8cc2-cf66bb1f3eeb':
        'https://dvtqxks0zalev.cloudfront.net/f3ddc025-faa4-41a7-a275-3c01fc87340e.png'
};

export const OsloDesignFairPage = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const queryParamData = useViewMapQueryParams();
    const ctxModal = useContext(CtxModal);
    const analytics = useAnalytics();

    const [allData, setAllData] = useState<GuideQuery>(null);

    useEffect(() => {
        const map = ctxView.mapRef.current;
        if (!map) {
            return;
        }

        function zoomEndHandler() {
            analytics.track('map.zoomed', {});
        }
        function moveEndHandler() {
            analytics.track('map.moved', {});
        }
        map?.on('zoomend', zoomEndHandler);
        map?.on('moveend', moveEndHandler);

        return () => {
            map?.off('zoomend', zoomEndHandler);
            map?.off('moveend', moveEndHandler);
        };
    }, [ctxView.mapRef?.current]);

    useEffect(() => {
        const link = new HttpLink({
            uri: 'https://welcome-api-prod-d7f8e7c14d20.herokuapp.com/graphql'
        });
        const apolloClient = new ApolloClient({
            link,
            defaultOptions: {
                watchQuery: {
                    refetchWritePolicy: 'overwrite'
                }
            },
            cache: new InMemoryCache({})
        });

        const exec = async () => {
            const resp = await apolloClient.query({
                query: OSLO_GUIDE_QUERY,
                variables: {
                    guideIdOrPath: 'b1c9e939-6d3b-4025-b432-e5c9b78c0038'
                }
            });

            const guide = resp.data?.app_guide;
            setAllData(resp.data);

            function getBounds() {
                if (guide.map?.initialPosition.__typename === 'MapInitialPositionBounds') {
                    return guide.map.initialPosition;
                }
                return null;
            }
            const bounds = getBounds();

            const attributeId = guide.map?.filters?.[0]?.attributeId;
            const attributeOptions =
                guide.entityAttributes?.find((e) => e.id === attributeId)?.options ?? [];

            const x: WorldMapView_worldMapView = {
                worldMapId: 'b1c9e939-6d3b-4025-b432-e5c9b78c0038',
                user: {
                    userId: '1',
                    __typename: 'UserMiniInfo',
                    imageUrl: '',
                    backgroundUrl: ''
                },
                __typename: 'WorldMapView',
                title: guide.title,
                markerDetailStyle: WorldMapMarkerDetailStyle.MAP_V2,
                customMapUrl: '',
                groupMarkerColor: '#000',
                groupMarkerBackground: '#000',
                groupMarkerClusterMaxZoom: 20,
                groupMarkerClusterRadius: 1,
                fillCountryCodes: [],
                fillCountryColor: '#000',
                fillCountryOpacity: 1,
                autoHideIcons: true,
                imageBackgroundUrl: null,
                showHighlightedCategory: false,
                showList: true,
                showSearch: true,
                imageBackgroundCoordinates: null,
                showOnMapZoom: guide.map?.locateMarkerZoom ?? 0,
                allowGeolocation: guide.map?.enableGpsLocation ?? false,
                visibility: EVisibility.PUBLIC,
                worldMapSets: attributeOptions.map((e, i) => {
                    const detailsWithOption = resp.data?.app_guide?.details?.filter((detail) => {
                        const attr = detail.attributes.find(
                            (attr) => attr.attributeId === attributeId
                        );
                        if (!attr) {
                            return [];
                        }
                        const options =
                            attr.__typename === 'DetailAttributeValueOption'
                                ? [attr.valueOption]
                                : attr.__typename === 'DetailAttributeValueOptions'
                                ? attr.valueOptions
                                : [];
                        return options.includes(e.id);
                    });

                    return {
                        title: e.title ?? '',
                        type: EWorldMapSetType.CATEGORY,
                        __typename: 'WorldMapSet',
                        isVisibleOnLoad: true,
                        imageUrl: CATEGORY_IMAGES[e.id] ?? null,
                        color: '#000',
                        orderIndex: i,
                        worldMapSetId: e.id,
                        markers: detailsWithOption.map((o) => ({
                            worldMapMarkerId: o.id,
                            __typename: 'WorldMapMarker'
                        }))
                    };
                }),
                initial: {
                    __typename: 'WorldMapInitial',
                    bounds: {
                        __typename: 'WorldMapInitialBounds',
                        topLeft: {
                            lat: bounds?.neLat ?? 0,
                            lng: bounds?.neLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        bottomRight: {
                            lat: bounds?.swLat ?? 0,
                            lng: bounds?.swLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        padding: 0
                    },
                    useBounds: true,
                    autoBounds: {
                        __typename: 'WorldMapInitialBounds',
                        topLeft: {
                            lat: bounds?.neLat ?? 0,
                            lng: bounds?.neLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        bottomRight: {
                            lat: bounds?.swLat ?? 0,
                            lng: bounds?.swLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        padding: 0
                    },
                    rotation: guide.map.initialRotation ?? 0,
                    pitch: guide.map?.initialPitch ?? 0,
                    maxZoom: 25,
                    minZoom: 1,
                    lat: 20,
                    lng: 20,
                    zoom: 13
                },
                initialMobile: {
                    __typename: 'WorldMapInitial',
                    bounds: {
                        __typename: 'WorldMapInitialBounds',
                        topLeft: {
                            lat: bounds?.neLat ?? 0,
                            lng: bounds?.neLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        bottomRight: {
                            lat: bounds?.swLat ?? 0,
                            lng: bounds?.swLng ?? 0,
                            __typename: 'Coordinates'
                        },
                        padding: 0
                    },
                    useBounds: true,
                    rotation: guide.map.initialRotation ?? 0,
                    pitch: guide.map?.initialPitch ?? 0,
                    maxZoom: 25,
                    minZoom: 1,
                    lat: 20,
                    lng: 20,
                    zoom: 13
                },
                initialType: EWorldMapInitialType.AUTOMATIC,
                defaultStyles: ['mapbox://styles/koudelka/cm55sn8e5003m01po72bte2wy?optimize=true'],
                guide: {
                    guideId: 'b1c9e939-6d3b-4025-b432-e5c9b78c0038',
                    title: guide?.title,
                    __typename: 'Guide',
                    availableLanguages: ['no'],
                    language: 'no'
                }
            };
            ctxView.setMap(x);
            ctxView.setVisibleMapSetIds([]);

            ctxView.setMarkers(
                resp.data.app_guide.details
                    .map((e) => {
                        const marker = guide.map.markers.find<F_MapMarkerIndoorStandFragment>(
                            (m): m is F_MapMarkerIndoorStandFragment =>
                                (m.__typename === 'MapMarkerIndoorStand' ||
                                    m.__typename === 'MapMarkerVariantIcon') &&
                                e.connectedMarkerIds.includes(m.id)
                        );
                        if (!marker) {
                            return null;
                        }
                        return {
                            worldMapMarkerId: e.id,
                            __typename: 'WorldMapMarker' as const,
                            layerTitle: '',
                            type: EWorldMapMarkerType.INDOOR_STAND,
                            gptContextId: null,
                            worldMapSets: [],
                            story: null,
                            lat: marker?.lat ?? 0,
                            lng: marker?.lng ?? 0,
                            placeId: null,
                            placeText: null,
                            locked: null,
                            scale: null,
                            isGroupable: null,
                            visible: true,
                            scalable: null,
                            rotate: null,
                            roomName: null,
                            isExternalPoint: null,
                            isRoomMarker: null,
                            order: null,
                            hasBookingConfiguration: null,
                            isBookingAvailable: null,
                            data: {
                                __typename: 'WorldMapMarkerDataIconV2'
                            } as any,
                            stories: [
                                {
                                    storyId: e.id,
                                    importKey: '',
                                    __typename: 'PublishedStory' as const,
                                    isExternalPoint: false,
                                    header: {
                                        imageUrl: e.imageUrl,
                                        title: e.title,
                                        text: e.description,
                                        __typename: 'StoryHeader' as const,
                                        imageStyleId: '1'
                                    },
                                    user: {
                                        __typename: 'UserMiniInfo' as const,
                                        userId: ''
                                    },
                                    categories: (() => {
                                        const attr = e.attributes.find(
                                            (attr) => attr.attributeId === attributeId
                                        );
                                        if (!attr) {
                                            return [];
                                        }
                                        const options =
                                            attr.__typename === 'DetailAttributeValueOption'
                                                ? [attr.valueOption]
                                                : attr.__typename === 'DetailAttributeValueOptions'
                                                ? attr.valueOptions
                                                : [];
                                        return options.map((e) => ({
                                            worldMapSetId: e,
                                            __typename: 'WorldMapSet' as const
                                        }));
                                    })(),
                                    connectedMarkers: [
                                        {
                                            worldMapMarkerId: e.id,
                                            __typename: 'WorldMapMarker',
                                            type: EWorldMapMarkerType.INDOOR_STAND
                                        }
                                    ]
                                }
                            ]
                        };
                    })
                    .filter(isDefined)
            );
            ctxView.setAllDetails(
                resp.data.app_guide.details
                    ?.map((e) => {
                        if (e.connectedMarkerIds.length === 0) {
                            return null;
                        }
                        return {
                            storyId: e.id,
                            importKey: '',
                            __typename: 'PublishedStory' as const,
                            isExternalPoint: false,
                            header: {
                                imageUrl: e.imageUrl,
                                title: e.title,
                                text: e.description,
                                __typename: 'StoryHeader' as const,
                                imageStyleId: '1'
                            },
                            user: {
                                __typename: 'UserMiniInfo' as const,
                                userId: ''
                            },
                            categories: (() => {
                                const attr = e.attributes.find(
                                    (attr) => attr.attributeId === attributeId
                                );
                                if (!attr) {
                                    return [];
                                }
                                const options =
                                    attr.__typename === 'DetailAttributeValueOption'
                                        ? [attr.valueOption]
                                        : attr.__typename === 'DetailAttributeValueOptions'
                                        ? attr.valueOptions
                                        : [];
                                return options.map((e) => ({
                                    worldMapSetId: e,
                                    __typename: 'WorldMapSet' as const
                                }));
                            })(),
                            connectedMarkers: [
                                {
                                    worldMapMarkerId: e.id,
                                    __typename: 'WorldMapMarker' as const,
                                    type: EWorldMapMarkerType.V2_ICON
                                }
                            ],
                            cards: e.cards
                                .map<WorldMapStories_publishedStoriesForWorldMap_cards | null>(
                                    (card) => {
                                        if (card.__typename === 'ContentCardText') {
                                            return {
                                                cardId: card.id,
                                                __typename: 'TextCard',
                                                info: {
                                                    title: card.title,
                                                    description: card.text,
                                                    __typename: 'CardInfo'
                                                },
                                                type: ECardType.TEXT
                                            };
                                        }
                                        if (card.__typename === 'ContentCardExternalLinks') {
                                            return {
                                                cardId: card.id,
                                                __typename: 'LinkCard',
                                                links: card?.links?.map((link) => ({
                                                    title: link?.label,
                                                    url: link?.url,
                                                    __typename: 'Link'
                                                })),
                                                type: ECardType.LINK
                                            };
                                        }
                                        if (card.__typename === 'ContentCardImages') {
                                            return {
                                                cardId: card.id,
                                                __typename: 'ImageCard',
                                                images: card?.imageUrls?.map((url) => ({
                                                    __typename: 'Image',
                                                    imageUrl: url,
                                                    fullImageUrl: url
                                                })),
                                                containImage: false,
                                                type: ECardType.IMAGE
                                            };
                                        }

                                        return null;
                                    }
                                )
                                .filter(isDefined)
                        };
                    })
                    .filter(isDefined)
            );
        };
        exec();
    }, []);

    useEffect(() => {
        if (queryParamData.language) {
            Localization.setLanguage(queryParamData.language);
        }
    }, [queryParamData, queryParamData.language]);

    if (!ctxView.map || !ctxView.allDetails || !ctxView.allMarkers) {
        return null;
    }

    if (!allData) {
        return null;
    }

    return (
        <S_ViewMapPageV2 className={props.className} $listEnabled={ctxView.map.showList}>
            <CtxMapV2Provider>
                <ThemeProvider
                    theme={{
                        colors: {
                            light: '#fafafa',
                            dark: '#464952',
                            dark2: '#2A2A2A',
                            grey: '#F2F2F2',
                            grey2: '#B6B8BC',
                            grey3: '#767676',
                            primary: '#049DEE',
                            primarySub: '#D3F0FF',
                            white: '#FFFFFF',
                            black: '#171717'
                        },
                        shadows: {
                            blockShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)'
                        },
                        fonts: {
                            medium: 'InterMedium',
                            semiBold: 'InterSemiBold'
                        },
                        gradients: {
                            secondary: 'linear-gradient(180deg, #F7A376 0%, #E77057 100%)'
                        }
                    }}
                >
                    <CtxMapViewProvider
                        mapRef={ctxView.mapRef}
                        onMarkersClicked={(ids) => {
                            const detail = allData.app_guide.details.find((e) =>
                                e.connectedMarkerIds.includes(ids[0])
                            );

                            ctxModal.setDetailModal({
                                id: detail?.id,
                                detail: ctxView.allDetails.find((e) => e.storyId === detail?.id),
                                markerId: detail.connectedMarkerIds[0]
                            });
                        }}
                    >
                        <OsloDesignFairMap allData={allData} />
                    </CtxMapViewProvider>
                </ThemeProvider>
            </CtxMapV2Provider>
        </S_ViewMapPageV2>
    );
};

const S_ViewMapPageV2 = styled(S_PageStyle)<{ $listEnabled: boolean }>`
    @media only screen and (max-width: 1259px) {
        .mapboxgl-ctrl-bottom-right {
            right: ${(props) => (props.$listEnabled ? '-5px' : '35px')};
            margin-bottom: ${(props) => (props.$listEnabled ? '1000px' : '0px')};
        }
        .mapboxgl-ctrl-bottom-left {
            left: -10px;
            margin-bottom: ${(props) => (props.$listEnabled ? '85px' : '-5px')};
        }
        .mapbox-geolocate {
            margin-bottom: ${(props) => (props.$listEnabled ? '112px' : '-5px')};
            margin-right: -12px;
        }
    }
`;
